import React, { useEffect, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import JsBarcode from "jsbarcode";
import tunisianCities from "../../../../utils/cities";

// Print settings for A5 landscape
const PrintSettings = () => {
    return (
        <style type="text/css" media="print">
            {`
        @page {
          size: A5 landscape;
          margin: 0;
        }
        
        body {
          margin: 0;
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }
        
        @media print {
          html, body {
            width: 210mm;
            margin: 0;
            padding: 0;
          }
          
          .print-container {
            display: flex;
            flex-wrap: wrap;
            width: 210mm !important;
          }
          
          .exchange-sheet {
            width: 210mm !important;
            min-height: 148.5mm !important;
            page-break-inside: avoid;
            overflow: hidden;
            page-break-after: always;
          }
        }
      `}
        </style>
    );
};

// Multiple exchanges export component
const ExchangeExportMultiple = ({ exchanges = [], logo, mylerzLogo }) => {
    return (
        <>
            <PrintSettings />
            <div className="print-container">
                {exchanges.map((exchange, index) => (
                    <ExchangeSheet
                        key={exchange.entet?.id || index}
                        exchange={exchange}
                        logo={logo}
                        mylerzLogo={mylerzLogo}
                    />
                ))}
            </div>
        </>
    );
};

// Single exchange sheet component
const ExchangeSheet = ({ exchange = {}, logo, mylerzLogo }) => {
    const [barcodeUrl, setBarcodeUrl] = useState("");

    useEffect(() => {
        console.log('Exchange data in ExchangeSheet:', exchange);
    }, [exchange]);

    // Enhanced data extraction to handle different API response formats
    // If exchange has "entet" and "line" structure, use that
    // Otherwise use the flatter structure with direct properties and "items"

    // Extract exchange header information - support both structures
    const isNestedFormat = !!exchange.entet;
    const exchangeHeader = isNestedFormat ? exchange.entet : exchange;

    // Extract line items - support both "line" and "items" arrays
    const exchangeLines = isNestedFormat
        ? exchange.line || []
        : exchange.items || [];

    // NEW: First check if we have client info in bonCommande, then fallback to originalOrder
    const originalBonCommande = exchangeHeader.bonCommande || {};
    const originalOrder = exchange.originalOrder || {};

    // Get client name - first try bonCommande, then originalOrder
    const clientFirstName = originalBonCommande.clientFirstName || originalOrder.clientFirstName || "";
    const clientLastName = originalBonCommande.clientLastName || originalOrder.clientLastName || "Client";
    const clientName = `${clientFirstName} ${clientLastName}`.trim();

    // Get client phone - first try bonCommande, then originalOrder
    const clientPhone = originalBonCommande.clientNumber || originalOrder.clientPhone || "N/A";

    // Get exchange reference
    const exchangeReference = exchangeHeader.reference || "ECH-XXXX";

    // Get original order reference - try different formats
    const orderReference = originalBonCommande.reference ||
        (exchangeHeader.bonCommandeId ? `CMD-${exchangeHeader.bonCommandeId}` : "N/A");

    // Format monetary values - ensure we're getting them from the right place
    const exchangePrice = parseFloat(exchangeHeader.resteAPayer || 0).toFixed(3);
    const totalHt = parseFloat(exchangeHeader.totalHt || 0).toFixed(3);
    const totalTva = parseFloat(exchangeHeader.totalTva || 0).toFixed(3);
    const fraisDeLivraison = parseFloat(exchangeHeader.fraisDeLivraison || 0).toFixed(3);
    const totalTTC = parseFloat(exchangeHeader.totalTTC || 0).toFixed(3);
    const totalRemise = parseFloat(exchangeHeader.totalRemise || 0).toFixed(3);
    const resteAPayer = parseFloat(exchangeHeader.resteAPayer || 0).toFixed(3);

    // Extract barcode information
    const exchangeBarCode = exchangeHeader.reference ? exchangeHeader.reference.replace('ECH-', '') : "000000";

    // Format date
    const exchangeDate = exchangeHeader.dateEchange
        ? new Date(exchangeHeader.dateEchange).toLocaleString('fr-FR')
        : new Date().toLocaleString('fr-FR');

    // Extract delivery information - first try bonCommande, then originalOrder
    const cityCode = originalBonCommande.ville || originalOrder.ville || "TUN";
    const cityName = getCityName(cityCode);
    const deliveryCityText = `DELIVERY ${cityCode} - ${cityCode}`;
    const deliveryAddress = originalBonCommande.clientAdresse || originalOrder.clientAdresse || "Adresse non disponible";
    const fullAddress = cityName ? `${cityName}, ${deliveryAddress}` : deliveryAddress;

    // Function to get city name from city code
    function getCityName(cityCode) {
        if (!cityCode) return 'N/A';

        const city = tunisianCities.find(city => city.Code === cityCode);
        return city ? city.EnName : cityCode;
    }

    // Process exchange lines to handle both data structures
    // In the flatter structure, use "aEchange" property
    // In the nested structure, adapt accordingly
    const exchangedProducts = exchangeLines.filter(line => {
        // Try both properties as different APIs may use different property names
        return line.aEchange === true;
    });

    const newProducts = exchangeLines.filter(line => {
        return line.aEchange === false;
    });

    // Format product info for display - handle different property names
    const formatProductInfo = (line) => {
        const productName = line.product?.name || "Produit";
        const variantName = line.productVariant?.name || "";
        const reference = line.productVariant?.reference || line.product?.reference || "N/A";

        // Helpers for promo-aware price extraction
        const parseFlexibleNumber = (v) => {
            if (v === null || v === undefined) return 0;
            if (typeof v === 'number') return v;
            const s = String(v).trim().replace(',', '.');
            const n = parseFloat(s);
            return isNaN(n) ? 0 : n;
        };

        const isTruthyFlag = (v) => {
            if (v === true || v === 1) return true;
            if (typeof v === 'string') {
                const l = v.toLowerCase().trim();
                return l === 'true' || l === '1' || l === 'yes' || l === 'y';
            }
            return false;
        };

        // Prefer promo price when enPromo flag is present
        const promoFlag = isTruthyFlag(line.enPromo) || isTruthyFlag(line.EnPromo) || isTruthyFlag(line.product?.enPromo) || isTruthyFlag(line.product?.EnPromo);
        const promoCandidates = [line.prixVentePromo, line.PrixVentePromo, line.prixPromo, line.product?.prixVentePromo, line.productVariant?.prixVentePromo];
        let price = 0;
        if (promoFlag) {
            for (const cand of promoCandidates) {
                const n = parseFlexibleNumber(cand);
                if (n > 0) {
                    price = n;
                    break;
                }
            }
        }

        if (!price) {
            // Handle price based on different API formats
            price = parseFlexibleNumber(line.prixUnitaire || line.price || line.prixVenteTTC || 0);
        }

        return {
            name: productName,
            reference: reference,
            attributes: variantName,
            quantity: line.quantite || line.quantity || 1,
            price: typeof price === 'number' ? price.toFixed(3) : "0.000",
            isExchanged: line.aEchange === true
        };
    };

    // Get all formatted products
    const allProducts = exchangeLines.map(formatProductInfo);

    // Exchange weight - can be adjusted based on your data
    const weight = "1.0"; // Default weight if not available

    // Check if we have a delivery code
    const hasDeliveryCode = !!exchangeHeader.codeLivraison;
    const deliveryCode = exchangeHeader.codeLivraison || exchangeBarCode;

    // Generate barcode as data URL using JsBarcode
    useEffect(() => {
        if (deliveryCode) {
            try {
                // Create a canvas for the barcode
                const canvas = document.createElement('canvas');

                // Ensure we have valid text for the barcode
                const validBarcodeText = deliveryCode.trim() || "0000000000";

                // Generate the barcode with error handling
                JsBarcode(canvas, validBarcodeText, {
                    format: "CODE128",
                    displayValue: false,
                    height: 40,
                    width: 2,
                    margin: 0,
                    background: "#ffffff"
                });

                // Convert to data URL and update state
                const url = canvas.toDataURL('image/png');
                setBarcodeUrl(url);
            } catch (error) {
                console.error("Failed to generate barcode:", error);

                // Fallback to a simple placeholder barcode
                const fallbackCanvas = document.createElement('canvas');
                const ctx = fallbackCanvas.getContext('2d');
                fallbackCanvas.width = 200;
                fallbackCanvas.height = 40;

                // Draw alternating bars
                ctx.fillStyle = '#ffffff';
                ctx.fillRect(0, 0, fallbackCanvas.width, fallbackCanvas.height);
                ctx.fillStyle = '#000000';

                for (let i = 0; i < 20; i++) {
                    if (i % 2 === 0) {
                        ctx.fillRect(i * 10, 0, 5, fallbackCanvas.height);
                    }
                }

                setBarcodeUrl(fallbackCanvas.toDataURL('image/png'));
            }
        }
    }, [deliveryCode]);

    // Calculate dynamic height based on total products
    const baseHeight = 148;
    const additionalHeight = Math.max(0, allProducts.length - 1) * 15;
    const sheetHeight = Math.max(baseHeight, baseHeight + additionalHeight);

    // Log structured debug information to help diagnose
    useEffect(() => {
        console.log('Exchange structured debug info:', {
            format: isNestedFormat ? 'Nested (entet/line)' : 'Flat (items)',
            header: {
                id: exchangeHeader.id,
                reference: exchangeHeader.reference,
                description: exchangeHeader.description,
                totalTTC: exchangeHeader.totalTTC,
                resteAPayer: exchangeHeader.resteAPayer
            },
            bonCommande: originalBonCommande ? {
                id: originalBonCommande.id,
                reference: originalBonCommande.reference,
                clientFirstName: originalBonCommande.clientFirstName,
                clientLastName: originalBonCommande.clientLastName,
                clientNumber: originalBonCommande.clientNumber,
                clientAdresse: originalBonCommande.clientAdresse,
                ville: originalBonCommande.ville
            } : 'No original bonCommande data',
            lines: {
                count: exchangeLines.length,
                exchangedCount: exchangedProducts.length,
                newCount: newProducts.length
            },
            clientInfo: {
                clientName,
                clientPhone,
                address: fullAddress
            }
        });
    }, [exchange]);

    return (
        <div className="exchange-sheet">
            <div
                style={{
                    width: "210mm",
                    minHeight: `${sheetHeight}mm`,
                    padding: "8mm",
                    fontFamily: "Arial, sans-serif",
                    fontSize: "12px",
                    background: "#fff",
                    boxSizing: "border-box",
                    position: "relative",
                    borderRadius: "2mm",
                    display: "flex",
                    flexDirection: "column",
                    border: "1px solid #000",
                }}
            >
                {/* ÉCHANGE label */}
                <div style={{
                    position: "absolute",
                    top: "5mm",
                    right: "10mm",
                    color: "#e53e3e",
                    fontWeight: "bold",
                    fontSize: "24px",
                    transform: "rotate(15deg)",
                    border: "3px solid #e53e3e",
                    padding: "6px 12px",
                    borderRadius: "6px",
                    backgroundColor: "rgba(255, 255, 255, 0.9)",
                    boxShadow: "0 3px 6px rgba(0, 0, 0, 0.2)",
                    zIndex: "5"
                }}>
                    ÉCHANGE
                </div>

                {/* Original order reference */}
                <div style={{
                    position: "absolute",
                    top: "7mm",
                    left: "10mm",
                    backgroundColor: "#f3f4f6",
                    border: "1px solid #d1d5db",
                    borderRadius: "4px",
                    padding: "3px 6px",
                    fontSize: "11px",
                    fontWeight: "bold",
                }}>
                    Commande d'origine: {orderReference}
                </div>

                {/* Top section with logo box and client/delivery information */}
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "6mm", marginTop: "10mm" }}>
                    {/* Left side - QR Code and client info */}
                    <div style={{ width: "35%", display: "flex", flexDirection: "column" }}>
                        {/* QR Code - only display if order.codeLivraison exists */}
                        {hasDeliveryCode && (
                            <div style={{
                                width: "20mm",
                                height: "20mm",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                marginBottom: "2mm",
                                border: "1px solid #ddd",
                                borderRadius: "1mm"
                            }}>
                                <QRCodeSVG
                                    value={deliveryCode}
                                    size={65}
                                    level="H"
                                />
                            </div>
                        )}
                        <div style={{ fontWeight: "bold", fontSize: "13px" }}>{clientName}</div>
                        <div style={{ fontWeight: "bold", fontSize: "13px" }}>{clientPhone}</div>
                        <div style={{ fontWeight: "bold", fontSize: "13px" }}>Référence : {exchangeReference}</div>
                    </div>

                    {/* Center - Logo Box */}
                    <div style={{
                        width: "28%",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        background: "linear-gradient(to bottom, #ffffff, #f9f9f9)",
                        border: "1px solid #d0d0d0",
                        boxShadow: "0 2px 5px rgba(0, 0, 0, 0.05)",
                        padding: "1mm",
                        borderRadius: "3mm"
                    }}>
                        <div style={{
                            width: "100%",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            marginBottom: "0.5mm"
                        }}>
                            <img
                                src={mylerzLogo}
                                alt="Mylerz"
                                style={{
                                    height: "14mm",
                                    maxWidth: "95%",
                                    objectFit: "contain",
                                    filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.1))"
                                }}
                            />
                        </div>

                        <div style={{
                            width: "90%",
                            height: "1px",
                            background: "#aaaaaa",
                            margin: "0.5mm 0",
                        }}></div>

                        <div style={{
                            width: "100%",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            marginTop: "0.5mm"
                        }}>
                            <img
                                src={logo}
                                alt="Logo"
                                style={{
                                    height: "13mm",
                                    maxWidth: "95%",
                                    objectFit: "contain",
                                    filter: "drop-shadow(0 1px 1px rgba(0,0,0,0.1))"
                                }}
                            />
                        </div>
                    </div>

                    {/* Right side - Delivery info */}
                    <div style={{ width: "35%", textAlign: "right", position: "relative" }}>
                        <div style={{ fontWeight: "bold", fontSize: "13px" }}>{deliveryCityText || "DELIVERY TUN - TUN"}</div>
                        <div style={{ fontWeight: "bold", fontSize: "13px" }}>CASH-ON-DELIVERY</div>
                        <div style={{
                            fontSize: "16px",
                            fontWeight: "bold",
                            marginTop: "3mm",
                            color: "#0f172a"
                        }}>
                            {exchangePrice} TND
                        </div>
                        {/* Authorization text */}
                        <div style={{
                            marginTop: "3mm",
                            fontStyle: "italic",
                            fontSize: "11px",
                            color: "#444",
                            backgroundColor: "rgba(255, 255, 255, 0.8)",
                            padding: "1mm",
                            borderRadius: "1mm",
                            border: "1px solid #eee",
                            fontWeight: "bold",
                            boxShadow: "0 1px 2px rgba(0, 0, 0, 0.05)",
                            display: "inline-block",
                            maxWidth: "fit-content",
                            marginTop: "10mm"
                        }}>
                            Autorisation d'ouverture de coli
                        </div>
                    </div>
                </div>

                {/* Divider */}
                <div style={{ width: "100%", borderTop: "1px solid #000", marginBottom: "4mm" }}></div>

                {/* Address section */}
                <div style={{ marginBottom: "4mm", textAlign: "center" }}>
                    <div style={{
                        fontWeight: "bold",
                        fontSize: "14px",
                        display: "inline-block",
                        padding: "2mm 4mm",
                        borderRadius: "2mm"
                    }}>
                        {fullAddress}
                    </div>
                </div>

                {/* Another divider */}
                <div style={{ width: "100%", borderTop: "1px solid #000", marginBottom: "4mm" }}></div>

                {/* Reference and barcode section */}
                <div style={{
                    borderRadius: "2mm",
                    padding: "3mm",
                    marginBottom: "3mm",
                    flexGrow: 1,
                    position: "relative"
                }}>

                    {/* Centered barcode section */}
                    <div style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        marginBottom: "3mm"
                    }}>
                        <div style={{
                            color: "#000",
                            padding: "1mm 6mm",
                            fontWeight: "bold",
                            fontSize: "12px",
                            marginBottom: "2mm",
                            textAlign: "center"
                        }}>
                            {weight} Kg / {exchangeDate}
                        </div>

                        {/* Barcode */}
                        {barcodeUrl ? (
                            <img
                                src={barcodeUrl}
                                alt={deliveryCode}
                                style={{
                                    width: "200px",
                                    height: "40px",
                                    display: "block"
                                }}
                            />
                        ) : (
                            <SimpleBarcode value={deliveryCode || exchangeReference} width={200} height={40} />
                        )}

                        <div style={{ textAlign: "center", fontWeight: "500", marginTop: "2mm", fontSize: "13px" }}>
                            BLF : {deliveryCode || exchangeReference}
                        </div>
                    </div>

                    <div>
                        {/* Only show new products */}
                        {newProducts.length > 0 && (
                            <div>
                                <div style={{
                                    fontWeight: "bold",
                                    marginBottom: "2mm",
                                    fontSize: "13px",
                                    borderRadius: "2mm",
                                    padding: "1mm 2mm",
                                    border: "1px solid #d1d5db"
                                }}>
                                    Produits ({newProducts.reduce((sum, l) => sum + (formatProductInfo(l).quantity || 1), 0)} {newProducts.reduce((sum, l) => sum + (formatProductInfo(l).quantity || 1), 0) > 1 ? 'articles' : 'article'}):
                                </div>

                                <div style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: "1mm",
                                    padding: "0", /* Removed padding here */
                                    borderRadius: "1mm"
                                }}>
                                    {newProducts.flatMap((line, lineIdx) => {
                                        const product = formatProductInfo(line);
                                        return Array.from({ length: product.quantity }, (_, unitIdx) => {
                                            const flatIndex = newProducts.slice(0, lineIdx).reduce((sum, l) => sum + (formatProductInfo(l).quantity || 1), 0) + unitIdx;
                                            return (
                                            <div
                                                key={`new-${lineIdx}-${unitIdx}`}
                                                style={{
                                                    padding: "1mm", /* Kept minimal padding */
                                                    border: "1px solid #d1d5db",
                                                    borderRadius: "1mm",
                                                    background: "#ffffff",
                                                    marginBottom: "0" /* Ensure no margin at bottom */
                                                }}
                                            >
                                                <div style={{
                                                    fontSize: "11px",
                                                    lineHeight: "1.1",
                                                    margin: "0" /* Remove margin */
                                                }}>
                                                    <span style={{ fontWeight: "bold" }}>Produit {flatIndex + 1}: </span>
                                                    {product.name} {product.attributes && `- ${product.attributes}`}
                                                    {product.reference && ` (${product.reference})`}
                                                    <span style={{ float: "right", fontWeight: "bold" }}>{product.price} TND</span>
                                                </div>
                                            </div>
                                            );
                                        });
                                    })}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

// Simple barcode component for fallback
const SimpleBarcode = ({ value, width = 200, height = 40 }) => {
    // Generate a checksum from the value
    const checksum = [...value].reduce((acc, char) => acc + char.charCodeAt(0), 0) % 10;

    // Create a visual pattern based on the value
    const bars = [];
    for (let i = 0; i < value.length; i++) {
        const charCode = value.charCodeAt(i);
        const barWidth = (charCode % 3) + 1;
        const space = (charCode % 2) + 1;

        bars.push({
            width: barWidth,
            space: space
        });
    }

    // Add start, end and checksum
    bars.unshift({ width: 2, space: 1 }); // start
    bars.push({ width: 1, space: 0 }); // checksum
    bars.push({ width: 2, space: 0 }); // end

    // Calculate total width
    const totalWidth = bars.reduce((acc, bar) => acc + bar.width + bar.space, 0);
    const scale = width / totalWidth;

    return (
        <svg width={width} height={height}>
            <rect width={width} height={height} fill="white" />
            {bars.reduce((acc, bar, i) => {
                const x = acc;
                const barWidthScaled = bar.width * scale;
                const spaceWidthScaled = bar.space * scale;

                return acc + barWidthScaled + spaceWidthScaled;
            }, 0)}

            {bars.map((bar, i) => {
                const x = bars.slice(0, i).reduce((acc, b) => acc + (b.width + b.space) * scale, 0);
                return <rect key={i} x={x} y={0} width={bar.width * scale} height={height} fill="black" />;
            })}
        </svg>
    );
};

// Single exchange export helper component
const ExchangeExportA5 = ({ exchange = {}, logo, mylerzLogo }) => {
    return (
        <ExchangeExportMultiple
            exchanges={[exchange]}
            logo={logo}
            mylerzLogo={mylerzLogo}
        />
    );
};

export { ExchangeExportMultiple, ExchangeExportA5 };
export default ExchangeExportA5;